import { useEffect, useState } from "react";
import { api, inr, fmtDate } from "@/lib/api";
import PageHeader, { EmptyState } from "@/components/PageHeader";
import StatusChip from "@/components/StatusChip";
import { toast } from "sonner";
import RecordActions from "@/components/RecordActions";

import { Plus, Handshake } from "@phosphor-icons/react";

export default function Purchase() {
  const [orders, setOrders] = useState([]);
  const [reqs, setReqs] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [tab, setTab] = useState("po");
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({ supplier_id: "", delivery_date: "", items: [{ material_id: "", name: "", quantity: 1, unit: "meter", rate: 0, tax: 0 }] });

  const load = async () => {
    try {
      const [po, r, c, m] = await Promise.all([
        api.get("/purchase/orders"),
        api.get("/purchase/requisitions"),
        api.get("/contacts?contact_type=supplier"),
        api.get("/inventory/materials"),
      ]);
      setOrders(po.data); setReqs(r.data); setContacts(c.data); setMaterials(m.data);
    } catch { toast.error("Load failed"); }
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    const items = form.items.map((it) => ({ ...it, quantity: Number(it.quantity), rate: Number(it.rate), tax: Number(it.tax) }));
    try { await api.post("/purchase/orders", { ...form, items }); toast.success("PO created"); setShowNew(false); load(); }
    catch { toast.error("Failed"); }
  };

  const approve = async (id) => {
    try { await api.post(`/purchase/orders/${id}/approve`); toast.success("PO approved"); load(); }
    catch { toast.error("Approval failed"); }
  };

  const receive = async (po) => {
    if (!confirm("Mark all lines fully received?")) return;
    try {
      await api.post(`/purchase/orders/${po._id}/receive`, { items: po.items || [], status: "fully_received" });
      toast.success("Goods received & inventory updated"); load();
    } catch { toast.error("Failed"); }
  };

  return (
    <div data-testid="purchase-page" className="page-fade">
      <PageHeader eyebrow="Module" title="Purchase" description="Requisitions, purchase orders, goods receipt and vendor tracking — reconciled with inventory." tint="forest" icon={Handshake} actions={
        <button className="btn-primary" onClick={()=>setShowNew(true)} data-testid="new-po-btn"><Plus size={14}/> New purchase order</button>
      }/>
      <div className="tab-list mb-6">
        {[["po","Purchase orders"],["req","Requisitions"]].map(([v,l])=>(
          <button key={v} onClick={()=>setTab(v)} data-testid={`purchase-tab-${v}`} className={tab===v?"is-active":""}>{l}</button>
        ))}
      </div>

      {tab === "po" && (
        <div className="card-soft p-0 overflow-x-auto">
          <table className="table-modern">
            <thead><tr><th>PO#</th><th>Supplier</th><th>Items</th><th>Total</th><th>Status</th><th>Delivery</th><th></th></tr></thead>
            <tbody>
              {orders.map((o) => {
                const sup = contacts.find(c => c._id === o.supplier_id);
                return (
                  <tr key={o._id} data-testid={`po-row-${o.po_number}`}>
                    <td className="font-mono text-xs">{o.po_number}</td>
                    <td className="font-medium">{sup?.name || o.supplier_id}</td>
                    <td className="tabular">{o.items?.length || 0}</td>
                    <td className="tabular font-medium">{inr(o.total)}</td>
                    <td><StatusChip value={o.status}/></td>
                    <td className="text-xs text-muted-foreground">{o.delivery_date || "—"}</td>
                    <td className="text-right">
                      <div className="flex gap-1.5 justify-end">
                        {o.status === "approval_pending" && <button onClick={()=>approve(o._id)} className="btn-ghost btn-sm" data-testid={`po-approve-${o.po_number}`}>Approve</button>}
                        {["approved","sent_to_supplier","partially_received"].includes(o.status) && <button onClick={()=>receive(o)} className="btn-ghost btn-sm" data-testid={`po-receive-${o.po_number}`}>Receive</button>}
                        <RecordActions entity="purchase_orders" record={o} frozen={["po_number"]} onChanged={load} />
                      </div>
                    </td>
                  </tr>
                );
              })}
              {!orders.length && <tr><td colSpan={7}><EmptyState icon={Handshake} title="No purchase orders yet" description="Create your first PO to source materials from suppliers." /></td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {tab === "req" && (
        <div className="card-soft p-0 overflow-x-auto">
          <table className="table-modern">
            <thead><tr><th>PR#</th><th>Department</th><th>Requested by</th><th>Items</th><th>Priority</th><th>Status</th><th>When</th><th></th></tr></thead>
            <tbody>
              {reqs.map((r) => (
                <tr key={r._id} data-testid={`pr-row-${r.pr_number}`}>
                  <td className="font-mono text-xs">{r.pr_number}</td>
                  <td className="capitalize">{r.department}</td>
                  <td>{r.requested_by}</td>
                  <td className="tabular">{r.items?.length || 0}</td>
                  <td className="capitalize text-sm">{r.priority}</td>
                  <td><StatusChip value={r.status}/></td>
                  <td className="text-xs text-muted-foreground">{fmtDate(r.created_at)}</td>
                  <td className="text-right whitespace-nowrap"><RecordActions entity="requisitions" record={r} frozen={["pr_number"]} onChanged={load} /></td>
                </tr>
              ))}
              {!reqs.length && <tr><td colSpan={7}><EmptyState icon={Handshake} title="No requisitions yet" description="Requisitions raised by teams will show here." /></td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {showNew && (
        <div className="fixed inset-0 z-50 modal-backdrop flex items-center justify-center p-4 fade-in" onClick={()=>setShowNew(false)}>
          <form onSubmit={submit} onClick={(e)=>e.stopPropagation()} className="modal-surface modal-scale p-7 w-full max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="new-po-form">
            <div className="label-eyebrow">New purchase order</div>
            <div className="display-3 mt-1 mb-6">Create</div>
            <label className="block"><span className="label-eyebrow">Supplier</span>
              <select required value={form.supplier_id} onChange={(e)=>setForm({...form, supplier_id: e.target.value})}
                      className="input-soft mt-2" data-testid="po-supplier">
                <option value="">— Select supplier —</option>
                {contacts.map(c=><option key={c._id} value={c._id}>{c.name} — {c.company}</option>)}
              </select>
            </label>
            <label className="block mt-5"><span className="label-eyebrow">Delivery date</span>
              <input type="date" value={form.delivery_date} onChange={(e)=>setForm({...form, delivery_date:e.target.value})}
                     className="input-underline" data-testid="po-delivery-date"/></label>
            <div className="label-eyebrow mt-8 mb-3">Items</div>
            {form.items.map((it, idx) => (
              <div key={idx} className="grid grid-cols-5 gap-2 mb-2">
                <select value={it.material_id} onChange={(e)=>{const items=[...form.items]; const mat=materials.find(m=>m._id===e.target.value); items[idx].material_id=e.target.value; items[idx].name=mat?.name||""; items[idx].unit=mat?.unit||"piece"; setForm({...form,items});}}
                        className="input-cell col-span-2" data-testid={`po-item-mat-${idx}`}>
                  <option value="">— Material —</option>
                  {materials.map(m=><option key={m._id} value={m._id}>{m.code} · {m.name}</option>)}
                </select>
                <input placeholder="Qty" type="number" value={it.quantity} onChange={(e)=>{const items=[...form.items]; items[idx].quantity=e.target.value; setForm({...form,items});}} className="input-cell"/>
                <input placeholder="Rate" type="number" value={it.rate} onChange={(e)=>{const items=[...form.items]; items[idx].rate=e.target.value; setForm({...form,items});}} className="input-cell"/>
                <input placeholder="Tax" type="number" value={it.tax} onChange={(e)=>{const items=[...form.items]; items[idx].tax=e.target.value; setForm({...form,items});}} className="input-cell"/>
              </div>
            ))}
            <button type="button" onClick={()=>setForm({...form, items:[...form.items, { material_id:"", name:"", quantity:1, unit:"meter", rate:0, tax:0 }]})} className="btn-ghost btn-sm mt-2">+ Add line</button>
            <div className="flex gap-2 mt-8">
              <button type="button" onClick={()=>setShowNew(false)} className="btn-ghost flex-1">Cancel</button>
              <button type="submit" className="btn-primary flex-1" data-testid="po-submit">Create PO</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
